// Missing financial inputs must stay unknown: Number(null) would turn them into zero.
export function getFiniteNumber(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function getPositionCostBasis(position) {
  return getFiniteNumber(position?.summe);
}

export function getPositionMarketValue(position) {
  const quantity = getFiniteNumber(position?.menge);
  const price = getFiniteNumber(position?.kurs);
  if (quantity === null || price === null || quantity < 0 || price < 0) return null;
  const value = quantity * price;
  return Number.isFinite(value) ? value : null;
}

export function getPositionProfitLoss(position) {
  const value = getPositionMarketValue(position);
  const cost = getPositionCostBasis(position);
  if (value === null || cost === null) return null;
  const result = value - cost;
  return Number.isFinite(result) ? result : null;
}

export function getPositionProfitLossPercent(position) {
  const cost = getPositionCostBasis(position);
  const result = getPositionProfitLoss(position);
  return cost > 0 && result !== null ? (result / cost) * 100 : null;
}

export function getProfitLossTotal(rows) {
  const values = rows.map(getPositionProfitLoss);
  return values.length && values.every(value => value !== null)
    ? values.reduce((sum, value) => sum + value, 0) : null;
}

export function getProfitLossPercentTotal(rows) {
  const result = getProfitLossTotal(rows);
  const cost = rows.reduce((sum, row) => sum + (getPositionCostBasis(row) || 0), 0);
  return result !== null && cost > 0 ? (result / cost) * 100 : null;
}

export function getPositionAnalysis(position) {
  const source = position?.analysis;
  if (source && typeof source === 'object') {
    return {
      exitPl: getFiniteNumber(source.exit_pl),
      exitPlPercent: getFiniteNumber(source.exit_pl_percent),
      unsafeGain: getFiniteNumber(source.unsafe_gain),
      distancePercent: getFiniteNumber(source.distance_percent),
    };
  }
  const current = getFiniteNumber(position?.kurs);
  const entry = getFiniteNumber(position?.einstandskurs);
  const quantity = getFiniteNumber(position?.menge);
  const cost = getFiniteNumber(position?.summe);
  const threshold = getFiniteNumber(position?.ma26);
  if ([current, entry, quantity, cost, threshold].some(value => value === null) || quantity <= 0 || current <= 0) {
    return { exitPl: null, exitPlPercent: null, unsafeGain: null, distancePercent: null };
  }
  const exitPl = (threshold - entry) * quantity;
  return {
    exitPl,
    exitPlPercent: cost > 0 ? (exitPl / cost) * 100 : null,
    unsafeGain: (threshold - current) * quantity,
    distancePercent: ((threshold - current) / current) * 100,
  };
}
